import {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import useEnvironmentContextProvider from '../../capabilities/contextStore/EnvironmentContext/useEnvironmentContextProvider';
import generateEventObject from '../../capabilities/checkout-v2/utils/generate-event-object';
import PciSessionContextUpdateApi from '../../services/pciSessionContext/PciSessionContextUpdateApi';
import { AppContext } from '../../capabilities/contextStore/AppContext';

interface UsePciSessionContextProps {
  pciActive?: boolean;
}

const usePciSessionContext = ({
  pciActive = false,
}: UsePciSessionContextProps = {}) => {
  const { postMessage } = useEnvironmentContextProvider();
  const { checkoutSessionId } = useContext(AppContext);
  const hasInitializedRef = useRef(false);
  const [isPciActivating, setIsPciActivating] = useState(false);

  const handlePciDeactivation = useCallback(() => {
    if (postMessage) {
      postMessage({
        type: 'onEvent',
        data: generateEventObject({
          title: 'SESSION_CONTEXT_UPDATED',
          sessionContext: { pci: { active: false } },
        }),
      });
    }
    PciSessionContextUpdateApi.updatePciSessionContext({
      sessionId: checkoutSessionId,
      payload: {
        operation: 'UPDATE_SESSION_PCI_FLAG',
        sessionContext: {
          pci: { active: false },
        },
      },
    }).catch((error: unknown) => {
      // eslint-disable-next-line no-console
      console.error(
        'Failed to update PCI session context:',
        error,
      );
    });
  }, [checkoutSessionId, postMessage]);

  useEffect(() => {
    const shouldActivate =
      pciActive && !hasInitializedRef.current;
    if (shouldActivate) {
      hasInitializedRef.current = true;
      if (postMessage) {
        postMessage({
          type: 'onEvent',
          data: generateEventObject({
            title: 'SESSION_CONTEXT_UPDATED',
            sessionContext: { pci: { active: true } },
          }),
        });
      }
      setIsPciActivating(true);
      PciSessionContextUpdateApi.updatePciSessionContext({
        sessionId: checkoutSessionId,
        payload: {
          operation: 'UPDATE_SESSION_PCI_FLAG',
          sessionContext: {
            pci: { active: true },
          },
        },
      })
        .catch((error: unknown) => {
          // eslint-disable-next-line no-console
          console.error(
            'Failed to update PCI session context:',
            error,
          );
        })
        .finally(() => {
          setIsPciActivating(false);
        });
    }

    const shouldDeactivate =
      !pciActive && hasInitializedRef.current;
    if (shouldDeactivate) {
      hasInitializedRef.current = false;
      handlePciDeactivation();
    }
  }, [
    pciActive,
    postMessage,
    checkoutSessionId,
    handlePciDeactivation,
  ]);

  useEffect(() => {
    return () => {
      if (hasInitializedRef.current) {
        handlePciDeactivation();
      }
    };
  }, [handlePciDeactivation]);

  return { isPciActivating };
};

export default usePciSessionContext;
